import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';

const TransitionContext = createContext({ 
  start: async (_path) => {},
  startWithImage: async (_path, _imageData) => {},
  imageTransitionData: null
});

export const usePageTransition = () => useContext(TransitionContext);

export const TransitionProvider = ({ children }) => {
  const navigate = useNavigate();
  const [imageTransitionData, setImageTransitionData] = useState(null);
  
  // Reset imageTransitionData after it's been used (after transition completes)
  useEffect(() => {
    if (imageTransitionData) {
      const timer = setTimeout(() => {
        setImageTransitionData(null);
      }, 2000); // Reset after 2 seconds (enough time for page to use it)
      return () => clearTimeout(timer);
    }
  }, [imageTransitionData]);

  const start = useCallback(async (path) => {
    return new Promise((resolve) => {
      // Create preloader elements
      const preloaderDiv = document.createElement('div');
      preloaderDiv.className = 'preloader';
      preloaderDiv.style.position = 'fixed';
      preloaderDiv.style.top = '0';
      preloaderDiv.style.left = '0';
      preloaderDiv.style.width = '100vw';
      preloaderDiv.style.height = '100vh';
      preloaderDiv.style.backgroundColor = '#262624';
      preloaderDiv.style.zIndex = '9999';
      preloaderDiv.style.display = 'flex';
      preloaderDiv.style.alignItems = 'center';
      preloaderDiv.style.justifyContent = 'center';

      const loaderDiv = document.createElement('div');
      loaderDiv.className = 'loader';
      
      const progressDiv = document.createElement('div');
      progressDiv.className = 'loader-progress';
      progressDiv.style.position = 'absolute';
      progressDiv.style.bottom = '24px';
      progressDiv.style.left = '50%';
      progressDiv.style.transform = 'translateX(-50%)';
      progressDiv.style.color = '#f2f3d9';
      progressDiv.style.fontFamily = 'Space Grotesk, sans-serif';
      progressDiv.style.fontWeight = '700';
      progressDiv.style.fontSize = '18px';
      progressDiv.style.letterSpacing = '-0.4px';
      progressDiv.textContent = '0%';

      preloaderDiv.appendChild(loaderDiv);
      preloaderDiv.appendChild(progressDiv);
      document.body.appendChild(preloaderDiv);

      // Animate percentage
      const progressObj = { value: 0 };
      gsap.to(progressObj, {
        value: 100,
        duration: 1.2,
        ease: 'linear',
        onUpdate: () => {
          const v = Math.min(100, Math.round(progressObj.value));
          progressDiv.textContent = v + '%';
        }
      });

      // Rotate loader
      gsap.to(loaderDiv, {
        rotation: '+=180',
        duration: 1.2,
        ease: 'power4.inOut',
        onComplete: () => {
          // Reset scroll before navigation
          const lenis = typeof window !== 'undefined' ? window.__lenis : null;
          if (lenis?.scrollTo) {
            lenis.scrollTo(0, { immediate: true, lock: true });
          } else {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          }
          
          // Navigate when loader completes
          navigate(path);
          
          // Scale down loader and clip preloader
          gsap.to(loaderDiv, {
            scale: 0,
            duration: 0.8,
            ease: 'power4.inOut'
          });

          gsap.to(preloaderDiv, {
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
            duration: 1,
            ease: 'power4.inOut',
            onComplete: () => {
              if (preloaderDiv && preloaderDiv.parentNode) {
                preloaderDiv.remove();
              }
              // Ensure scroll is at top after transition completes
              if (lenis?.scrollTo) {
                lenis.scrollTo(0, { immediate: true, lock: false });
              }
              resolve();
            }
          });
        }
      });
    });
  }, [navigate]);

  const startWithImage = useCallback(async (path, imageData) => {
    return new Promise((resolve) => {
      if (!imageData) {
        // Use regular start if no image data
        start(path).then(resolve);
        return;
      }

      // Store image data for the destination page
      setImageTransitionData(imageData);

      const { textElements } = imageData;

      // Fade out text elements first
      if (textElements && textElements.length > 0) {
        gsap.to(textElements, {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.out'
        });
      }

      // Create preloader elements (same as start function)
      const preloaderDiv = document.createElement('div');
      preloaderDiv.className = 'preloader';
      preloaderDiv.style.position = 'fixed';
      preloaderDiv.style.top = '0';
      preloaderDiv.style.left = '0';
      preloaderDiv.style.width = '100vw';
      preloaderDiv.style.height = '100vh';
      preloaderDiv.style.backgroundColor = '#262624';
      preloaderDiv.style.zIndex = '9999';
      preloaderDiv.style.display = 'flex';
      preloaderDiv.style.alignItems = 'center';
      preloaderDiv.style.justifyContent = 'center';

      const loaderDiv = document.createElement('div');
      loaderDiv.className = 'loader';
      
      const progressDiv = document.createElement('div');
      progressDiv.className = 'loader-progress';
      progressDiv.style.position = 'absolute';
      progressDiv.style.bottom = '24px';
      progressDiv.style.left = '50%';
      progressDiv.style.transform = 'translateX(-50%)';
      progressDiv.style.color = '#f2f3d9';
      progressDiv.style.fontFamily = 'Space Grotesk, sans-serif';
      progressDiv.style.fontWeight = '700';
      progressDiv.style.fontSize = '18px';
      progressDiv.style.letterSpacing = '-0.4px';
      progressDiv.textContent = '0%';

      preloaderDiv.appendChild(loaderDiv);
      preloaderDiv.appendChild(progressDiv);
      document.body.appendChild(preloaderDiv);

      // Animate percentage
      const progressObj = { value: 0 };
      gsap.to(progressObj, {
        value: 100,
        duration: 1.2,
        ease: 'linear',
        onUpdate: () => {
          const v = Math.min(100, Math.round(progressObj.value));
          progressDiv.textContent = v + '%';
        }
      });

      // Rotate loader
      gsap.to(loaderDiv, {
        rotation: '+=180',
        duration: 1.2,
        ease: 'power4.inOut',
        onComplete: () => {
          // Reset scroll before navigation
          const lenis = typeof window !== 'undefined' ? window.__lenis : null;
          if (lenis?.scrollTo) {
            lenis.scrollTo(0, { immediate: true, lock: true });
          } else {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          }
          
          // Navigate when loader completes
          navigate(path);
          
          // Scale down loader and clip preloader
          gsap.to(loaderDiv, {
            scale: 0,
            duration: 0.8,
            ease: 'power4.inOut'
          });

          gsap.to(preloaderDiv, {
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
            duration: 1,
            ease: 'power4.inOut',
            onComplete: () => {
              if (preloaderDiv && preloaderDiv.parentNode) {
                preloaderDiv.remove();
              }
              // Ensure scroll is at top after transition completes
              if (lenis?.scrollTo) {
                lenis.scrollTo(0, { immediate: true, lock: false });
              }
              resolve();
            }
          });
        }
      });
    });
  }, [navigate, start]);

  const value = useMemo(() => ({ 
    start, 
    startWithImage,
    imageTransitionData 
  }), [start, startWithImage, imageTransitionData]);

  return (
    <TransitionContext.Provider value={value}>
      {children}
    </TransitionContext.Provider>
  );
};

export default TransitionProvider;


